import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  CompanyListedFilter,
  CompanyListResponse,
  CompanyRecipient,
  ReportCatalog,
  ReportSchedule,
  ReportScheduleListParams,
  ReportScheduleListResponse,
  ReportScheduleSubmitPayload,
  ReportSchedulerStatus,
} from '../types'

export async function getReportScheduleList(
  params: ReportScheduleListParams,
): Promise<ReportScheduleListResponse> {
  try {
    const response = await apiClient.get<ReportScheduleListResponse>('/api/reports_scheduler', {
      params,
    })
    return {
      data: response.data?.data ?? [],
      current: Number(response.data?.current ?? 1),
      last: Number(response.data?.last ?? 1),
      total: Number(response.data?.total ?? 0),
    }
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load report schedules')
  }
}

export async function getReportCatalog(): Promise<ReportCatalog> {
  try {
    const response = await apiClient.get<ReportCatalog>('/api/report_list')
    return response.data ?? {}
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load reports')
  }
}

export async function getCompaniesForSchedule(
  listed: CompanyListedFilter,
): Promise<CompanyRecipient[]> {
  try {
    const response = await apiClient.get<CompanyListResponse | CompanyRecipient[]>('/api/companylist', {
      params: { listed },
    })
    const payload = response.data
    if (Array.isArray(payload)) return payload
    return payload?.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load PMCs')
  }
}

export async function saveReportSchedule(
  payload: ReportScheduleSubmitPayload,
  scheduleId?: number,
): Promise<ReportSchedule> {
  try {
    const url = scheduleId ? `/api/reports_scheduler/${scheduleId}` : '/api/reports_scheduler'
    const response = await apiClient.post<ReportSchedule>(url, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save schedule')
  }
}

export async function updateReportScheduleStatus(
  scheduleId: number,
  reportStatus: ReportSchedulerStatus,
): Promise<ReportSchedule> {
  try {
    const response = await apiClient.post<ReportSchedule>(`/api/reports_scheduler/${scheduleId}`, {
      report_status: reportStatus,
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update schedule status')
  }
}

export async function deleteReportSchedule(scheduleId: number): Promise<void> {
  try {
    await apiClient.delete(`/api/reports_scheduler/${scheduleId}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete schedule')
  }
}

/** Paste image into email body — same as Angular `POST /api/emailcontent_file_upload` with `emailcontent` field. */
export async function uploadEmailContentImage(file: File): Promise<string> {
  try {
    const formData = new FormData()
    formData.append('emailcontent', file)
    const response = await apiClient.post<string>('/api/emailcontent_file_upload', formData)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to upload image')
  }
}
